import { GMB_BOOKING_URL } from '../constants';
import { useState } from 'react';

interface GMBNoProfileFallbackProps {
  onSkip: () => void;
  darkMode: boolean;
}

export default function GMBNoProfileFallback({ onSkip, darkMode }: GMBNoProfileFallbackProps) {
  const [showGuide, setShowGuide] = useState(false);

  return (
    <div className="flex items-center justify-center min-h-[500px]">
      <div
        className={`w-full max-w-lg rounded-2xl border shadow-sm p-8 ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}
      >
        <div
          className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 ${
            darkMode ? 'bg-amber-900/30' : 'bg-amber-50'
          }`}
        >
          <i className="ri-map-pin-line text-3xl text-amber-600"></i>
        </div>

        <h2 className={`text-xl font-semibold mb-2 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          We couldn't find a Google Business Profile
        </h2>

        <p className={`text-sm mb-6 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Your Google account connected successfully, but we didn't find a listing you have access to —
          either you don't have a Business Profile yet, or don't have the right level of access to it.
        </p>

        <div className={`rounded-lg p-4 mb-6 text-sm ${darkMode ? 'bg-gray-700/50 text-gray-300' : 'bg-amber-50 text-amber-800'}`}>
          Until your listing is verified, Google won't show it fully in search results or Maps.
          Verification is what confirms to Google that your business is real and at that address.
        </div>

        <div className="space-y-3">
          <a
            href={GMB_BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full py-3 px-4 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2"
          >
            <i className="ri-phone-line"></i>
            Book a free 15-minute call with our team
          </a>

          <button
            onClick={() => setShowGuide((v) => !v)}
            className={`w-full py-3 px-4 rounded-lg border font-semibold text-sm transition-colors flex items-center justify-center gap-2 ${
              darkMode
                ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <i className={`ri-arrow-${showGuide ? 'up' : 'down'}-s-line`}></i>
            Follow the self-serve guide instead
          </button>

          {showGuide && (
            <div className={`rounded-lg p-4 text-sm space-y-2 ${darkMode ? 'bg-gray-700/50 text-gray-300' : 'bg-gray-50 text-gray-600'}`}>
              <p>1. Create a Business Profile listing on Google, if you haven't already.</p>
              <p>2. Claim and verify your business — by postcard, phone, email, or instant verification, depending on what Google offers for your listing.</p>
              <p>3. Complete your profile with your business details.</p>
              <p>4. Come back here and reconnect once verification is complete.</p>
            </div>
          )}

          <button
            onClick={onSkip}
            className={`w-full text-sm transition-colors ${
              darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Set up later
          </button>
        </div>
      </div>
    </div>
  );
}
